/**
 * executeWithAuthRetry - Global API Wrapper with Auth Retry
 *
 * ISSUE_047: Centralized 401/JWT expired handling for all Supabase API calls
 *
 * Key Features:
 * - Wraps all Supabase API calls
 * - Detects 401/PGRST303/JWT expired errors
 * - Requests token refresh via AuthBroker
 * - Retries the operation once with fresh token
 *
 * @module core/api/executeWithAuthRetry
 */

import { refreshAuthToken } from '~/core/auth/AuthBroker';
import { touchQuerySuccess } from '~/core/resilience/HealthMonitor';

// =============================================================================
// SINGLE-FLIGHT DEDUPE (ISSUE_164 조치 C)
// =============================================================================
//
// 책임 분리:
// - AuthBroker.refreshToken() — fetch 호출 자체를 dedupe (1회 /auth/v1/token)
// - dedupedRefresh() (여기) — **결과 처리 / 콘솔 로그 / 표면화** 를 dedupe
//
// 왜 둘 다 필요한가:
//   ISSUE_161 δ — N개 컴포넌트가 동시에 401 → executeWithAuthRetry 가 N회 진입.
//   AuthBroker 가 fetch 1회로 합쳐주지만, 각 호출 지점은 여전히 자신의
//   continue / return / console.error 를 실행 → 로그 N회, 에러 표면화 N회.
//   여기서 isOwner 1회만 콘솔에 남기고, waiter 들은 동일 결과를 받되 침묵.
//
// finally 에서 inFlightRefresh=null 로 클리어 → 끝난 후 새 401 들어오면 새 refresh 진행.

interface DedupedRefreshResult {
  token: string | null;
  epoch: number;
  isOwner: boolean;
}

let inFlightRefresh: Promise<{ token: string | null; epoch: number }> | null = null;

async function dedupedRefresh(): Promise<DedupedRefreshResult> {
  if (inFlightRefresh) {
    const result = await inFlightRefresh;
    return { ...result, isOwner: false };
  }
  const promise = refreshAuthToken().finally(() => {
    if (inFlightRefresh === promise) inFlightRefresh = null;
  });
  inFlightRefresh = promise;
  const result = await promise;
  return { ...result, isOwner: true };
}

/** 테스트 헬퍼 — 모듈 스코프 단일 진입 상태를 리셋. 프로덕션 코드에서 사용 금지. */
export function __resetInFlightRefreshForTest(): void {
  inFlightRefresh = null;
}

// =============================================================================
// TYPES
// =============================================================================

export interface ApiResult<T> {
  data: T | null;
  error: ApiError | null;
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

export interface RetryOptions {
  /** Maximum number of retries (default: 1) */
  maxRetries?: number;
  /** Timeout in milliseconds (default: 30000) */
  timeout?: number;
  /** Whether to retry on auth errors (default: true) */
  retryOnAuth?: boolean;
}

/**
 * Supabase-compatible result type (more permissive than ApiResult)
 * Supabase returns { data, error } where error may have additional properties
 */
export interface SupabaseResult<T> {
  data: T | null;
  error: { message: string; code?: string; status?: number } | null;
}

// =============================================================================
// ERROR DETECTION
// =============================================================================

/**
 * Check if an error is an authentication error
 */
export function isAuthError(error: { message?: string; code?: string; status?: number } | null): boolean {
  if (!error) return false;

  // HTTP 401 Unauthorized
  if (error.status === 401) return true;

  // PostgREST JWT errors
  if (error.code === 'PGRST303') return true;
  if (error.code === 'PGRST302') return true;

  // Message-based detection
  const msg = error.message?.toLowerCase() || '';
  if (msg.includes('jwt expired')) return true;
  if (msg.includes('jwt claim is invalid')) return true;
  if (msg.includes('invalid claim')) return true;
  if (msg.includes('token is expired')) return true;
  if (msg.includes('not authorized')) return true;
  if (msg.includes('invalid api key')) return true;

  return false;
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: { message?: string } | null): boolean {
  if (!error) return false;

  const msg = error.message?.toLowerCase() || '';
  if (msg.includes('network')) return true;
  if (msg.includes('fetch')) return true;
  if (msg.includes('timeout')) return true;
  if (msg.includes('abort')) return true;
  if (msg.includes('failed to fetch')) return true;

  return false;
}

// =============================================================================
// MAIN FUNCTION
// =============================================================================

/**
 * Execute a Supabase operation with automatic auth retry
 *
 * @example
 * ```ts
 * const result = await executeWithAuthRetry(
 *   () => supabase.from('sessions').select('*')
 * );
 * ```
 */
export async function executeWithAuthRetry<T>(
  operation: () => PromiseLike<SupabaseResult<T>>,
  options: RetryOptions = {}
): Promise<ApiResult<T>> {
  const {
    maxRetries = 1,
    timeout = 30000,
    retryOnAuth = true,
  } = options;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Execute with timeout
      const result = await withTimeout(operation(), timeout);

      // Success case
      if (!result.error) {
        // Record success for health monitoring
        touchQuerySuccess();
        return result;
      }

      // Auth error - attempt refresh and retry
      if (isAuthError(result.error) && retryOnAuth && attempt < maxRetries) {
        const { token, isOwner } = await dedupedRefresh();

        if (!token) {
          // ISSUE_164 조치 C: in-flight owner 만 로깅 → 동시 N개 호출자에서 콘솔 1회만 출력.
          if (isOwner) console.error('[executeWithAuthRetry] Token refresh failed');
          return result; // Return original error
        }

        continue; // Retry with fresh token
      }

      // Non-auth error or retry exhausted
      return result;

    } catch (error) {
      // Handle exceptions (timeout, network error, etc.)
      const apiError: ApiError = {
        message: error instanceof Error ? error.message : 'Unknown error',
        code: 'NETWORK_ERROR',
      };

      // Network errors might be recoverable with auth refresh
      if (isNetworkError(apiError) && retryOnAuth && attempt < maxRetries) {
        try {
          // ISSUE_164 조치 C: 동시 호출 디듀프 (auth error 분기와 동일).
          const { token } = await dedupedRefresh();
          if (token) {
            continue;
          }
        } catch {
          // Refresh failed, return error
        }
      }

      return { data: null, error: apiError };
    }
  }

  // Should not reach here, but return error just in case
  return {
    data: null,
    error: { message: 'Max retries exceeded', code: 'RETRY_EXHAUSTED' },
  };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Wrap a promise with a timeout
 */
function withTimeout<T>(
  promise: Promise<T> | PromiseLike<T>,
  ms: number
): Promise<T> {
  // Check if offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return Promise.reject(new Error('Network is offline'));
  }

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Request timed out after ${ms}ms`));
    }, ms);

    Promise.resolve(promise)
      .then((result) => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

// =============================================================================
// CONVENIENCE WRAPPERS
// =============================================================================

/**
 * Execute a query with auth retry (shorthand for select operations)
 */
export async function queryWithAuthRetry<T>(
  operation: () => PromiseLike<SupabaseResult<T>>
): Promise<ApiResult<T>> {
  return executeWithAuthRetry(operation, { maxRetries: 1 });
}

/**
 * Supabase max_rows(기본 1000) 제한을 우회하여 전량 조회.
 * .range() 페이지네이션으로 pageSize 단위로 반복 요청하여 모든 행을 수집.
 *
 * @param buildQuery - (from, to) => 쿼리. 반드시 .range(from, to) 포함해야 함.
 * @param pageSize - 페이지 크기 (기본 1000)
 */
export async function paginatedQuery<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildQuery: (from: number, to: number) => PromiseLike<{ data: any; error: { message: string } | null }>,
  pageSize = 1000,
): Promise<T[]> {
  const allRows: T[] = [];
  let from = 0;

  for (;;) {
    const to = from + pageSize - 1;
    const { data, error } = await queryWithAuthRetry(() => buildQuery(from, to));
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as unknown as T[];
    allRows.push(...rows);
    if (rows.length < pageSize) break;
    from += pageSize;
  }

  return allRows;
}

/**
 * Execute a mutation with auth retry (shorthand for insert/update/delete)
 * Uses 2 retries for mutations to handle edge cases
 */
export async function mutateWithAuthRetry<T>(
  operation: () => PromiseLike<SupabaseResult<T>>
): Promise<ApiResult<T>> {
  return executeWithAuthRetry(operation, { maxRetries: 2, timeout: 60000 });
}

/**
 * Execute without auth retry (for operations that should not retry)
 */
export async function executeWithoutRetry<T>(
  operation: () => PromiseLike<SupabaseResult<T>>
): Promise<ApiResult<T>> {
  return executeWithAuthRetry(operation, { maxRetries: 0, retryOnAuth: false });
}

// =============================================================================
// RESILIENT EXECUTION (SDK DEADLOCK RECOVERY)
// =============================================================================

import { pingSupabase, resetSupabaseClient } from '~/shared/lib/supabase';

/** Track if we've already reset in this session to prevent loops */
let hasResetInSession = false;
let lastResetAt = 0;
const RESET_COOLDOWN = 5 * 60 * 1000; // 5분 후 재시도 허용

/**
 * ISSUE_047: Execute with SDK deadlock detection and recovery
 *
 * Flow:
 * 1. Try operation with 5s timeout
 * 2. On timeout/error, ping Supabase directly (bypass SDK)
 * 3. If ping OK but SDK failed → SDK deadlock → reset client → retry once
 * 4. If ping also fails → real network issue → throw error
 *
 * @example
 * ```ts
 * const data = await executeResilient(async () => {
 *   const { data, error } = await supabase.from('profiles').select('*');
 *   if (error) throw error;
 *   return data;
 * });
 * ```
 */
export async function executeResilient<T>(
  operation: () => Promise<T>,
  options: { timeout?: number; label?: string } = {}
): Promise<T> {
  const { timeout = 5000 } = options;

  try {
    // First attempt with timeout
    return await withTimeoutResilient(operation(), timeout);
  } catch (firstError) {
    // Prevent infinite reset loops — 쿨다운 이내면 스킵
    if (hasResetInSession && Date.now() - lastResetAt < RESET_COOLDOWN) {
      console.error(`[executeResilient] Already reset in this session, not retrying`);
      throw firstError;
    }

    // Check if it's SDK deadlock (ping OK) or real network issue (ping fails)
    const pingOk = await pingSupabase();

    if (!pingOk) {
      // Real network issue - don't reset client
      console.error(`[executeResilient] Ping failed - real network issue`);
      throw firstError;
    }

    // SDK deadlock detected - reset client and retry once
    hasResetInSession = true;
    lastResetAt = Date.now();
    resetSupabaseClient();

    // Small delay for client to stabilize
    await new Promise(resolve => setTimeout(resolve, 100));

    // Retry once with fresh client
    return await operation();
  }
}

/**
 * Wrap a promise with timeout for resilient execution
 */
function withTimeoutResilient<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Operation timed out after ${ms}ms`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

/**
 * Reset the session reset flag (call on successful operations or page load)
 */
export function resetResilientState(): void {
  hasResetInSession = false;
}
